/**
 * TNK-155 phase C — fetch a scoped slice of cards by id list.
 *
 * Resolution order:
 *   1. AppContext.cards (warm cache from sign-in hydration). Free.
 *   2. Single server fetch for any ids the cache doesn't have.
 *
 * Phase D removes the eager AppContext hydration; this hook continues
 * working since step 2 carries the load. Re-fires only when the id-set
 * actually changes (stable across array-identity churn).
 */
import { useEffect, useMemo, useState } from "react";

import { type Card, useApp } from "@/context/AppContext";
import { fetchCardsByIds } from "@/context/inventorySync";

interface UseCardsByIdsResult {
  phase: "loading" | "ready" | "error";
  /** Same order as the input `ids`. Missing ids are omitted. */
  cards: Card[];
  byId: Map<string, Card>;
  /** Ids the server didn't return after a completed fetch. Empty until
   *  phase === "ready". */
  missing: Set<string>;
}

export function useCardsByIds(ids: readonly string[]): UseCardsByIdsResult {
  const { cards: contextCards } = useApp();
  const [extras, setExtras] = useState<Map<string, Card>>(() => new Map());
  const [attempted, setAttempted] = useState<Set<string>>(() => new Set());
  const [errored, setErrored] = useState(false);

  const contextById = useMemo(() => {
    const m = new Map<string, Card>();
    for (const c of contextCards) m.set(c.id, c);
    return m;
  }, [contextCards]);

  const idKey = useMemo(() => [...ids].sort().join("|"), [ids]);

  useEffect(() => {
    setErrored(false);
    const toFetch = ids.filter(
      (id) => !contextById.has(id) && !extras.has(id) && !attempted.has(id),
    );
    if (toFetch.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const fetched = await fetchCardsByIds(toFetch);
        if (cancelled) return;
        setExtras((prev) => {
          const next = new Map(prev);
          for (const c of fetched) next.set(c.id, c);
          return next;
        });
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) {
          setAttempted((prev) => {
            const next = new Set(prev);
            for (const id of toFetch) next.add(id);
            return next;
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // idKey covers the ids axis; the maps are intentionally omitted to
    // avoid a re-fire after our own setExtras / setAttempted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey]);

  const byId = useMemo(() => {
    const m = new Map<string, Card>();
    for (const id of ids) {
      const fromCtx = contextById.get(id);
      if (fromCtx) {
        m.set(id, fromCtx);
        continue;
      }
      const fromExtras = extras.get(id);
      if (fromExtras) m.set(id, fromExtras);
    }
    return m;
  }, [ids, contextById, extras]);

  const cards = useMemo(() => {
    const out: Card[] = [];
    for (const id of ids) {
      const c = byId.get(id);
      if (c) out.push(c);
    }
    return out;
  }, [ids, byId]);

  // Still-fetching: any id we haven't seen in context AND haven't attempted.
  const stillFetching = ids.some(
    (id) => !contextById.has(id) && !attempted.has(id),
  );

  const missing = useMemo(() => {
    const out = new Set<string>();
    if (stillFetching) return out;
    for (const id of ids) {
      if (!byId.has(id)) out.add(id);
    }
    return out;
  }, [ids, byId, stillFetching]);

  const phase: UseCardsByIdsResult["phase"] = errored
    ? "error"
    : stillFetching
      ? "loading"
      : "ready";

  return { phase, cards, byId, missing };
}
